/**
 * HitZone component
 *
 * Renders the static hit-zone visual overlay that sits above the game canvas.
 * Shows which keyboard keys map to which lanes and animates when pressed.
 * Warm palette with amber, gold, and coral accents.
 */

import React, { useEffect, useState } from 'react';
import { LANE_KEYS } from '../../types';

interface HitZoneProps {
  activeLanes: Set<number>;
  lastRatings: Array<'PERFECT' | 'GOOD' | 'MISS' | null>;
}

const KEY_DISPLAY: Record<string, string> = {
  s: 'S',
  d: 'D',
  f: 'F',
  j: 'J',
  k: 'K',
  l: 'L',
};

// Warm color palette for lanes
const WARM_LANE_COLORS = [
  '#f5a623', // amber
  '#e8b86d', // gold
  '#e07a5f', // coral
  '#f5a623', // amber
  '#e8b86d', // gold
  '#e07a5f', // coral
];

const RATING_COLORS: Record<string, string> = {
  PERFECT: '#81b29a', // sage
  GOOD:    '#e8b86d', // gold
  MISS:    '#e07a5f', // coral
};

export const HitZone: React.FC<HitZoneProps> = ({
  activeLanes,
  lastRatings,
}) => {
  return (
    <div className="flex flex-col gap-1 py-2">
      {LANE_KEYS.map((key, lane) => {
        const isActive = activeLanes.has(lane);
        const rating = lastRatings[lane];
        const color = WARM_LANE_COLORS[lane];

        return (
          <div
            key={lane}
            className="flex items-center gap-2"
            style={{ height: '48px' }}
          >
            {/* Key button */}
            <div
              className="w-12 h-10 rounded-lg flex items-center justify-center
                         font-mono font-bold text-sm transition-all duration-75 select-none"
              style={{
                backgroundColor: isActive ? color + '25' : '#141414',
                border: `2px solid ${isActive ? color : color + '40'}`,
                color: isActive ? color : color + '80',
                boxShadow: isActive
                  ? `0 0 12px ${color}40`
                  : 'none',
                transform: isActive ? 'scale(0.95)' : 'scale(1)',
              }}
            >
              {KEY_DISPLAY[key]}
            </div>

            {/* Rating flash */}
            {rating && (
              <RatingFlash rating={rating} lane={lane} />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// RatingFlash sub-component
// ---------------------------------------------------------------------------

interface RatingFlashProps {
  rating: 'PERFECT' | 'GOOD' | 'MISS';
  lane: number;
}

const RatingFlash: React.FC<RatingFlashProps> = ({ rating }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 600);
    return () => clearTimeout(t);
  }, [rating]);

  if (!visible) return null;

  return (
    <span
      className="text-xs font-semibold animate-fade-in"
      style={{
        color: RATING_COLORS[rating],
      }}
    >
      {rating}
    </span>
  );
};
